import { useState, useRef, useCallback } from "react";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
interface Initiative {
  id: string;
  name: string;
  carbonReduction: number;
  startYear: number;
  endYear: number;
  color: string;
  status: string;
}

const CarbonInitiativeTimeline = () => {
  const years = [2023, 2024, 2025, 2026, 2027, 2028, 2029];
  const yearWidth = 100 / years.length;
  
  const [initiatives, setInitiatives] = useState<Initiative[]>([
    {
      id: "solar",
      name: "Solar Panel Installation",
      carbonReduction: 500,
      startYear: 2024,
      endYear: 2025,
      color: "hsl(142, 76%, 36%)",
      status: "planned"
    },
    {
      id: "energy",
      name: "Energy Efficiency Program",
      carbonReduction: 300,
      startYear: 2025,
      endYear: 2027,
      color: "hsl(217, 91%, 60%)",
      status: "planned"
    }
  ]);

  const containerRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState<{
    id: string;
    type: "move" | "resize-left" | "resize-right";
    startX: number;
    originalStart: number;
    originalEnd: number;
  } | null>(null);

  const getYearFromPosition = useCallback((clientX: number) => {
    if (!containerRef.current) return years[0];
    const rect = containerRef.current.getBoundingClientRect();
    const relativeX = clientX - rect.left;
    const percentage = relativeX / rect.width;
    const yearIndex = Math.round(percentage * years.length);
    return years[Math.max(0, Math.min(years.length - 1, yearIndex))];
  }, [years]);

  const handleMouseDown = (e: React.MouseEvent, id: string, type: "move" | "resize-left" | "resize-right") => {
    e.preventDefault();
    const initiative = initiatives.find(i => i.id === id);
    if (!initiative) return;
    
    setDragging({
      id,
      type,
      startX: e.clientX,
      originalStart: initiative.startYear,
      originalEnd: initiative.endYear
    });
  };

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!dragging || !containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const deltaX = e.clientX - dragging.startX;
    const yearsDelta = Math.round((deltaX / rect.width) * years.length);

    setInitiatives(prev => prev.map(initiative => {
      if (initiative.id !== dragging.id) return initiative;

      let newStart = initiative.startYear;
      let newEnd = initiative.endYear;

      if (dragging.type === "move") {
        const duration = dragging.originalEnd - dragging.originalStart;
        newStart = Math.max(years[0], Math.min(years[years.length - 1] - duration, dragging.originalStart + yearsDelta));
        newEnd = newStart + duration;
      } else if (dragging.type === "resize-left") {
        newStart = Math.max(years[0], Math.min(initiative.endYear - 1, dragging.originalStart + yearsDelta));
      } else if (dragging.type === "resize-right") {
        newEnd = Math.max(initiative.startYear + 1, Math.min(years[years.length - 1], dragging.originalEnd + yearsDelta));
      }

      return { ...initiative, startYear: newStart, endYear: newEnd };
    }));
  }, [dragging, years]);

  const handleMouseUp = useCallback(() => {
    setDragging(null);
  }, []);

  // Add event listeners for drag
  useState(() => {
    const handleGlobalMouseMove = (e: MouseEvent) => handleMouseMove(e);
    const handleGlobalMouseUp = () => handleMouseUp();

    if (dragging) {
      window.addEventListener("mousemove", handleGlobalMouseMove);
      window.addEventListener("mouseup", handleGlobalMouseUp);
    }

    return () => {
      window.removeEventListener("mousemove", handleGlobalMouseMove);
      window.removeEventListener("mouseup", handleGlobalMouseUp);
    };
  });

  // Calculate carbon reduction per year
  const calculateYearlyReduction = () => {
    return years.map(year => {
      let total = 0;
      initiatives.forEach(initiative => {
        if (year >= initiative.startYear && year <= initiative.endYear) {
          const duration = initiative.endYear - initiative.startYear + 1;
          total += initiative.carbonReduction / duration;
        }
      });
      return Math.round(total);
    });
  };

  const yearlyReductions = calculateYearlyReduction();
  const totalReduction = initiatives.reduce((sum, i) => sum + i.carbonReduction, 0);
  const avgReduction = Math.round(totalReduction / years.length);

  const getBarPosition = (startYear: number, endYear: number) => {
    const startIndex = years.indexOf(startYear);
    const endIndex = years.indexOf(endYear);
    const left = (startIndex / years.length) * 100;
    const width = ((endIndex - startIndex + 1) / years.length) * 100;
    return { left: `${left}%`, width: `${width}%` };
  };

  return (
    <div 
      className="w-full h-full bg-card rounded-xl p-3 sm:p-6 flex flex-col select-none overflow-x-auto scrollbar-hide"
      onMouseMove={(e) => dragging && handleMouseMove(e.nativeEvent)}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4 min-w-[500px] sm:min-w-0">
        <h3 className="text-base sm:text-xl font-bold text-foreground">Carbon Initiative Timeline</h3>
        <Button 
          variant="outline" 
          size="sm" 
          className="text-xs border-border hover:bg-accent hover:text-accent-foreground"
        >
          <Plus className="w-3 h-3 mr-1" />
          Add Project
        </Button>
      </div>
      
      {/* Header Row */}
      <div className="flex border-b border-border pb-3 mb-2 min-w-[500px] sm:min-w-0">
        <div className="w-[30%] sm:w-[35%] text-xs sm:text-sm font-semibold text-muted-foreground">Initiative</div>
        <div className="w-[70%] sm:w-[65%] flex" ref={containerRef}>
          {years.map(year => (
            <div key={year} className="flex-1 text-center text-xs sm:text-sm font-semibold text-muted-foreground">
              {year}
            </div>
          ))}
        </div>
      </div>

      {/* Initiative Rows */}
      <div className="flex-1 space-y-1 min-w-[500px] sm:min-w-0">
        {initiatives.map(initiative => {
          const position = getBarPosition(initiative.startYear, initiative.endYear);
          return (
            <div key={initiative.id} className="flex items-center py-2 sm:py-3 border-b border-border/50">
              <div className="w-[30%] sm:w-[35%] pr-2 sm:pr-4">
                <div className="flex items-start gap-1 sm:gap-2">
                  <div 
                    className="w-1 h-10 sm:h-12 rounded-full mt-1 flex-shrink-0" 
                    style={{ backgroundColor: initiative.color }}
                  />
                  <div className="min-w-0">
                    <p className="font-medium text-foreground text-xs sm:text-sm leading-tight truncate">{initiative.name}</p>
                    <p className="text-xs text-muted-foreground">{initiative.carbonReduction} tCO2e</p>
                    <span 
                      className="inline-block mt-1 px-1.5 sm:px-2 py-0.5 text-[10px] sm:text-xs font-medium rounded-full"
                      style={{ 
                        backgroundColor: "hsl(45, 93%, 47%)", 
                        color: "hsl(0, 0%, 100%)" 
                      }}
                    >
                      {initiative.status}
                    </span>
                  </div>
                </div>
              </div>
              <div className="w-[70%] sm:w-[65%] h-8 sm:h-10 relative bg-muted/30 rounded">
                {/* Year grid lines */}
                {years.map((_, i) => (
                  <div 
                    key={i} 
                    className="absolute top-0 bottom-0 w-px bg-border/30"
                    style={{ left: `${(i / years.length) * 100}%` }}
                  />
                ))}
                {/* Draggable Timeline Bar */}
                <div
                  className="absolute top-1 bottom-1 rounded-lg cursor-move flex items-center justify-center transition-all duration-75"
                  style={{
                    ...position,
                    backgroundColor: "hsl(45, 93%, 47%)",
                    boxShadow: dragging?.id === initiative.id ? "0 4px 12px rgba(0,0,0,0.15)" : "none"
                  }}
                  onMouseDown={(e) => handleMouseDown(e, initiative.id, "move")}
                >
                  {/* Left resize handle */}
                  <div 
                    className="absolute left-0 top-0 bottom-0 w-2 cursor-ew-resize hover:bg-white/30 rounded-l-lg"
                    onMouseDown={(e) => { e.stopPropagation(); handleMouseDown(e, initiative.id, "resize-left"); }}
                  />
                  {/* Label */}
                  <span className="text-xs font-medium text-white pointer-events-none">
                    {initiative.startYear}-{initiative.endYear}
                  </span>
                  {/* Right resize handle */}
                  <div 
                    className="absolute right-0 top-0 bottom-0 w-2 cursor-ew-resize hover:bg-white/30 rounded-r-lg"
                    onMouseDown={(e) => { e.stopPropagation(); handleMouseDown(e, initiative.id, "resize-right"); }}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Total Carbon Reduction Row */}
      <div className="flex items-center py-3 sm:py-4 mt-2 bg-primary/10 rounded-lg px-2 min-w-[500px] sm:min-w-0">
        <div className="w-[30%] sm:w-[35%] pr-2 sm:pr-4">
          <div className="flex items-start gap-1 sm:gap-2">
            <div className="w-1 h-8 sm:h-10 rounded-full mt-1 flex-shrink-0 bg-primary" />
            <div>
              <p className="font-bold text-primary text-xs sm:text-sm">Total Carbon Reduction</p>
              <p className="text-[10px] sm:text-xs text-primary/70">{totalReduction} tCO2e/year avg</p>
            </div>
          </div>
        </div>
        <div className="w-[70%] sm:w-[65%] flex">
          {yearlyReductions.map((reduction, i) => (
            <div key={i} className="flex-1 text-center">
              <span className="text-xs sm:text-sm font-semibold text-foreground">{reduction}</span>
              <span className="text-[10px] sm:text-xs text-muted-foreground block">tCO2e</span>
            </div>
          ))}
        </div>
      </div>

      {/* Drag instruction */}
      <p className="text-xs text-muted-foreground text-center mt-3">
        Drag bars to move • Drag edges to resize
      </p>
    </div>
  );
};

export default CarbonInitiativeTimeline;
